import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  documentId,
  deleteDoc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { DEFAULT_ROLES } from '../utils/permissions';
import { createSlug, isValidTenantSlug } from '../utils/slug';
import { normalizeCurriculumEntry } from '../utils/curriculum';
import { normalizeIntakeSessions } from '../utils/intakeSchedule';
import { normalizePhoneNumber } from '../utils/validators';

const TENANTS = 'tenants';
const USERS = 'users';
const SLUGS = 'slugs';
const TENANT_SLUGS = 'tenantSlugs';
const NOTIFICATION_READS = 'notificationReads';

export async function upsertUserDocument(user, data = {}) {
  const userRef = doc(db, USERS, user.uid);
  const snapshot = await getDoc(userRef);
  const base = {
    uid: user.uid,
    displayName: data.displayName || user.displayName || '',
    photoURL: user.photoURL || null,
    updatedAt: serverTimestamp(),
  };
  if (data.email || user.email) base.email = data.email || user.email;
  if (data.phone) base.phone = data.phone;
  if (data.country) base.country = data.country;
  if (data.countryCode) base.countryCode = data.countryCode;
  if (data.phoneCountry) base.phoneCountry = data.phoneCountry;
  if (data.phoneCountryCode) base.phoneCountryCode = data.phoneCountryCode;
  if (data.phoneCountryDialCode) base.phoneCountryDialCode = data.phoneCountryDialCode;
  if (data.timezone) base.timezone = data.timezone;
  if (data.currency) base.currency = data.currency;

  if (snapshot.exists()) {
    await setDoc(userRef, base, { merge: true });
  } else {
    await setDoc(userRef, { ...base, createdAt: serverTimestamp(), lastActiveTenantId: null }, { merge: true });
  }
}

export async function getUserDocument(uid) {
  const snapshot = await getDoc(doc(db, USERS, uid));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function getNotificationReadIds(userId, tenantId) {
  if (!userId || !tenantId) return [];
  const snapshot = await getDoc(doc(db, USERS, userId, NOTIFICATION_READS, tenantId));
  return snapshot.exists() && Array.isArray(snapshot.data().readIds) ? snapshot.data().readIds : [];
}

export async function markNotificationRead(userId, tenantId, notificationId) {
  if (!userId || !tenantId || !notificationId) return;
  const readRef = doc(db, USERS, userId, NOTIFICATION_READS, tenantId);
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(readRef);
    const readIds = snapshot.exists() && Array.isArray(snapshot.data().readIds) ? snapshot.data().readIds : [];
    if (!readIds.includes(notificationId)) {
      transaction.set(readRef, { readIds: [...readIds, notificationId], updatedAt: serverTimestamp() }, { merge: true });
    }
  });
}

export async function setLastActiveTenant(uid, tenantId) {
  await setDoc(doc(db, USERS, uid), { lastActiveTenantId: tenantId, updatedAt: serverTimestamp() }, { merge: true });
}

export async function ensureUserTenantMembershipMirror(userId, membership) {
  await setDoc(doc(db, USERS, userId, 'tenantMemberships', membership.tenantId), {
    tenantId: membership.tenantId,
    roleId: membership.roleId,
    status: membership.status,
    joinedAt: membership.joinedAt || serverTimestamp(),
  }, { merge: true });
}

export async function isTenantSlugAvailable(slug) {
  const [legacySnapshot, tenantSlugSnapshot] = await Promise.all([
    getDoc(doc(db, SLUGS, slug)),
    getDoc(doc(db, TENANT_SLUGS, slug)),
  ]);
  return !legacySnapshot.exists() && !tenantSlugSnapshot.exists();
}

export async function createTenant({ user, name, type, slug, timezone = 'Africa/Nairobi', currency = 'KES' }) {
  const tenantRef = doc(collection(db, TENANTS));
  const safeSlug = createSlug(slug || name);
  if (!isValidTenantSlug(safeSlug)) {
    const error = new Error('Use a safe academy URL with lowercase letters, numbers, and hyphens.');
    error.code = 'invalid-slug';
    throw error;
  }

  const slugRef = doc(db, TENANT_SLUGS, safeSlug);
  const legacySlugRef = doc(db, SLUGS, safeSlug);
  const tenantMemberRef = doc(db, TENANTS, tenantRef.id, 'members', user.uid);
  const userMembershipRef = doc(db, USERS, user.uid, 'tenantMemberships', tenantRef.id);
  const userRef = doc(db, USERS, user.uid);
  const tenantData = {
    id: tenantRef.id,
    name,
    slug: safeSlug,
    type,
    ownerId: user.uid,
    status: 'active',
    branding: {
      logoURL: null,
      primaryColor: null,
    },
    domain: {
      subdomain: safeSlug,
      customDomain: null,
      customDomainVerified: false,
    },
    plan: 'free',
    settings: {
      timezone,
      currency,
      publicTemplate: 'modern',
      publicWebsite: {
        heroTitle: '',
        heroDescription: '',
        heroImageURL: '',
        aboutTitle: '',
        aboutDescription: '',
      },
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await runTransaction(db, async (transaction) => {
    const [tenantSlugSnapshot, legacySlugSnapshot] = await Promise.all([
      transaction.get(slugRef),
      transaction.get(legacySlugRef),
    ]);

    if (tenantSlugSnapshot.exists() || legacySlugSnapshot.exists()) {
      const error = new Error('This academy URL is already taken.');
      error.code = 'slug-unavailable';
      throw error;
    }

    transaction.set(tenantRef, tenantData);
    transaction.set(slugRef, {
      slug: safeSlug,
      tenantId: tenantRef.id,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });
    transaction.set(legacySlugRef, {
      slug: safeSlug,
      tenantId: tenantRef.id,
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });

    DEFAULT_ROLES.forEach((role) => {
      transaction.set(doc(db, TENANTS, tenantRef.id, 'roles', role.id), {
        ...role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    transaction.set(tenantMemberRef, {
      userId: user.uid,
      email: user.email,
      displayName: user.displayName || '',
      roleId: 'owner',
      status: 'active',
      joinedAt: serverTimestamp(),
    });
    transaction.set(userMembershipRef, {
      tenantId: tenantRef.id,
      roleId: 'owner',
      status: 'active',
      joinedAt: serverTimestamp(),
    });
    transaction.set(userRef, {
      uid: user.uid,
      displayName: user.displayName || '',
      email: user.email,
      photoURL: user.photoURL || null,
      lastActiveTenantId: tenantRef.id,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });

  return { ...tenantData, id: tenantRef.id };
}

export async function getTenant(tenantId) {
  if (!tenantId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function getMembership(tenantId, userId) {
  if (!tenantId || !userId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'members', userId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function getRole(tenantId, roleId) {
  if (!tenantId || !roleId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'roles', roleId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function getUserMemberships(userId) {
  const membershipsSnapshot = await getDocs(query(collection(db, USERS, userId, 'tenantMemberships'), where('status', '==', 'active')));
  const memberships = membershipsSnapshot.docs.map((memberDoc) => ({ id: memberDoc.id, ...memberDoc.data() }));

  if (!memberships.length) return [];

  const tenantIds = memberships.map((membership) => membership.tenantId);
  const tenants = [];
  for (let i = 0; i < tenantIds.length; i += 10) {
    const chunk = tenantIds.slice(i, i + 10);
    const tenantsSnapshot = await getDocs(query(collection(db, TENANTS), where(documentId(), 'in', chunk)));
    tenants.push(...tenantsSnapshot.docs.map((tenantDoc) => ({ id: tenantDoc.id, ...tenantDoc.data() })));
  }
  return memberships.map((membership) => ({
    ...membership,
    tenant: tenants.find((tenant) => tenant.id === membership.tenantId),
  })).filter((membership) => membership.tenant);
}

export async function updateTenantSettings(tenantId, values) {
  const hasPublicWebsite = Object.prototype.hasOwnProperty.call(values, 'publicWebsite');
  const payload = {
    name: values.name,
    type: values.type,
    'settings.timezone': values.timezone,
    'settings.currency': values.currency,
    'settings.publicTemplate': values.publicTemplate || 'modern',
    'branding.primaryColor': values.primaryColor || null,
    updatedAt: serverTimestamp(),
  };
  if (hasPublicWebsite) {
    payload['settings.publicWebsite'] = values.publicWebsite || {};
  } else {
    payload['settings.publicWebsite.heroTitle'] = values.publicHeroTitle || '';
    payload['settings.publicWebsite.heroDescription'] = values.publicHeroDescription || '';
    payload['settings.publicWebsite.heroImageURL'] = values.publicHeroImageURL || '';
    payload['settings.publicWebsite.aboutTitle'] = values.publicAboutTitle || '';
    payload['settings.publicWebsite.aboutDescription'] = values.publicAboutDescription || '';
  }
  if (Object.prototype.hasOwnProperty.call(values, 'logoURL')) payload['branding.logoURL'] = values.logoURL || null;
  if (Object.prototype.hasOwnProperty.call(values, 'logoScale')) {
    const logoScale = Number(values.logoScale);
    payload['branding.logoScale'] = Number.isFinite(logoScale) ? Math.min(1.25, Math.max(0.75, logoScale)) : 1;
  }
  if (!values.skipFeePayment) payload['settings.feePayment'] = values.feePayment || {};
  await updateDoc(doc(db, TENANTS, tenantId), payload);
}

export async function updateTenantDomain(tenantId, customDomain) {
  await updateDoc(doc(db, TENANTS, tenantId), {
    'domain.customDomain': customDomain || null,
    'domain.customDomainVerified': false,
    updatedAt: serverTimestamp(),
  });
}

export async function createCourse(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'courses'), {
    name: values.name,
    code: values.code,
    description: values.description || '',
    duration: values.duration || '',
    durationValue: Number(values.durationValue || 0),
    durationUnit: values.durationUnit || 'weeks',
    feeAmount: Number(values.feeAmount || 0),
    imageURL: values.imageURL || '',
    paymentTerm: values.courseType === 'self_paced' ? 'full' : (values.paymentTerm || 'full'),
    courseType: values.courseType || 'live_online',
    status: values.publicEnrollmentEnabled ? 'active' : 'draft',
    instructorId: values.instructorId || null,
    instructorName: values.instructorName || '',
    publicEnrollment: {
      enabled: Boolean(values.publicEnrollmentEnabled),
      intakeMode: values.intakeMode || 'open',
      signupMessage: values.signupMessage || '',
    },
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateCoursePublicEnrollment(tenantId, courseId, enabled) {
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId), {
    status: enabled ? 'active' : 'draft',
    publicEnrollment: {
      enabled,
      intakeMode: 'open',
      signupMessage: '',
    },
    updatedAt: serverTimestamp(),
  });
}

export async function updateCourse(tenantId, courseId, values) {
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId), {
    name: values.name.trim(),
    code: values.code.trim().toUpperCase(),
    description: values.description?.trim() || '',
    duration: values.duration?.trim() || '',
    durationValue: Number(values.durationValue || 0),
    durationUnit: values.durationUnit || 'weeks',
    feeAmount: Number(values.feeAmount || 0),
    imageURL: values.imageURL || '',
    paymentTerm: values.courseType === 'self_paced' ? 'full' : (values.paymentTerm || 'full'),
    courseType: values.courseType || 'live_online',
    updatedAt: serverTimestamp(),
  });
}

export async function updateCourseInstructor(tenantId, courseId, values) {
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId), {
    instructorId: values.instructorId || null,
    instructorName: values.instructorName || '',
    updatedAt: serverTimestamp(),
  });
}

export async function archiveCourse(tenantId, courseId) {
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId), {
    status: 'archived',
    publicEnrollment: {
      enabled: false,
      intakeMode: 'open',
      signupMessage: '',
    },
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCourse(tenantId, courseId) {
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId), {
    status: 'deleted',
    publicEnrollment: {
      enabled: false,
      intakeMode: 'open',
      signupMessage: '',
    },
    updatedAt: serverTimestamp(),
  });
}

export async function getCourses(tenantId, countLimit = 50) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'courses'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createDigitalProduct(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'digitalProducts'), {
    name: values.name || '',
    courseId: values.courseId || null,
    courseName: values.courseName || '',
    format: values.format || 'course',
    price: Number(values.price || 0),
    delivery: values.delivery || 'Self-paced',
    description: values.description || '',
    status: values.status || 'draft',
    createdBy: userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getDigitalProducts(tenantId, countLimit = 100) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'digitalProducts'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getCourse(tenantId, courseId) {
  if (!tenantId || !courseId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'courses', courseId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function getPublicAcademyBySlug(slug) {
  const safeSlug = createSlug(slug);
  if (!safeSlug) return null;

  const [tenantSlugSnapshot, legacySlugSnapshot] = await Promise.all([
    getDoc(doc(db, TENANT_SLUGS, safeSlug)),
    getDoc(doc(db, SLUGS, safeSlug)),
  ]);

  const tenantId = tenantSlugSnapshot.exists()
    ? tenantSlugSnapshot.data().tenantId
    : legacySlugSnapshot.exists()
      ? legacySlugSnapshot.data().tenantId
      : null;

  if (!tenantId) return null;
  const tenant = await getTenant(tenantId);
  if (!tenant || tenant.status !== 'active') return null;
  return tenant;
}

export async function getPublicCourses(tenantId) {
  const snapshot = await getDocs(query(
    collection(db, TENANTS, tenantId, 'courses'),
    where('status', '==', 'active'),
    where('publicEnrollment.enabled', '==', true),
    limit(50),
  ));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createIntake(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'intakes'), {
    name: values.name,
    courseId: values.courseId,
    type: values.type || 'cohort',
    status: values.status || 'open',
    publicVisible: values.publicVisible !== false,
    startDate: values.startDate || '',
    endDate: values.endDate || '',
    capacity: Number(values.capacity || 0),
    sessions: normalizeIntakeSessions(values.sessions),
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getIntakes(tenantId, countLimit = 100) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'intakes'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getIntake(tenantId, intakeId) {
  if (!tenantId || !intakeId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'intakes', intakeId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function updateIntake(tenantId, intakeId, values) {
  await updateDoc(doc(db, TENANTS, tenantId, 'intakes', intakeId), {
    name: values.name || '',
    courseId: values.courseId || null,
    type: values.type || 'cohort',
    status: values.status || 'open',
    publicVisible: values.publicVisible !== false,
    startDate: values.startDate || '',
    endDate: values.endDate || '',
    capacity: Number(values.capacity || 0),
    sessions: normalizeIntakeSessions(values.sessions),
    updatedAt: serverTimestamp(),
  });
}

export async function getPublicIntakes(tenantId) {
  const snapshot = await getDocs(query(
    collection(db, TENANTS, tenantId, 'intakes'),
    where('status', '==', 'open'),
    where('publicVisible', '==', true),
    limit(100),
  ));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createCourseSignup(tenantId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'courseSignups'), {
    tenantId,
    fullName: values.fullName,
    firstName: values.firstName || '',
    lastName: values.lastName || '',
    courseId: values.courseId,
    courseName: values.courseName || '',
    intakeId: values.intakeId || null,
    intakeLabel: values.intakeLabel || 'Open intake',
    intakeSessionId: values.intakeSessionId || null,
    intakeSessionLabel: values.intakeSessionLabel || '',
    email: values.email,
    phone: normalizePhoneNumber(values.phone),
    gender: values.gender || '',
    countryOfResidence: values.countryOfResidence || '',
    yearOfBirth: values.yearOfBirth || '',
    studyMode: values.studyMode || '',
    message: values.message || '',
    authUid: values.authUid || null,
    authEmail: values.email,
    authStatus: values.authStatus || 'created',
    status: 'new',
    source: 'public_academy',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getCourseSignups(tenantId, countLimit = 100) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'courseSignups'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getCourseSignup(tenantId, signupId) {
  if (!tenantId || !signupId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'courseSignups', signupId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function updateCourseSignup(tenantId, signupId, values) {
  if (!tenantId || !signupId) return;
  await updateDoc(doc(db, TENANTS, tenantId, 'courseSignups', signupId), {
    ...values,
    updatedAt: serverTimestamp(),
  });
}

export async function getStudentCourseSignups(authUid, countLimit = 100) {
  if (!authUid) return [];
  const snapshot = await getDocs(query(collectionGroup(db, 'courseSignups'), where('authUid', '==', authUid), limit(countLimit)));
  return snapshot.docs.map((item) => ({
    id: item.id,
    tenantId: item.data().tenantId || item.ref.parent.parent?.id || '',
    ...item.data(),
  }));
}

export async function createCurriculumEntry(tenantId, courseId, values) {
  const normalized = normalizeCurriculumEntry(values);
  return addDoc(collection(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries'), {
    ...normalized,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getCurriculumEntries(tenantId, courseId, countLimit = 200) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function updateCurriculumEntry(tenantId, courseId, entryId, values) {
  const normalized = normalizeCurriculumEntry(values);
  await updateDoc(doc(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries', entryId), {
    ...normalized,
    updatedAt: serverTimestamp(),
  });
}

export async function updateCurriculumWeekObjective(tenantId, courseId, weekLabel, weeklyObjective) {
  if (!weekLabel) return;

  const snapshot = await getDocs(query(
    collection(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries'),
    where('weekLabel', '==', weekLabel),
  ));

  if (!snapshot.size) return;

  const batch = writeBatch(db);
  snapshot.docs.forEach((item) => {
    batch.update(doc(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries', item.id), {
      weeklyObjective: String(weeklyObjective || '').trim(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
}

export async function deleteCurriculumEntry(tenantId, courseId, entryId) {
  await deleteDoc(doc(db, TENANTS, tenantId, 'courses', courseId, 'curriculumEntries', entryId));
}

export async function replaceCurriculumEntries(tenantId, courseId, entries = []) {
  const existing = await getCurriculumEntries(tenantId, courseId, 500);
  await Promise.all(existing.map((entry) => deleteCurriculumEntry(tenantId, courseId, entry.id)));
  await Promise.all(entries.map((entry) => createCurriculumEntry(tenantId, courseId, entry)));
}

export async function createClass(tenantId, userId, values) {
  const locationType = values.locationType || 'online';
  return addDoc(collection(db, TENANTS, tenantId, 'classes'), {
    title: values.title,
    courseId: values.courseId,
    instructorId: values.instructorId || null,
    instructorName: values.instructorName || '',
    scheduleType: values.scheduleType || 'continuous',
    scheduledDate: values.scheduledDate || '',
    startTime: values.startTime || '08:00',
    endTime: values.endTime || '10:00',
    recurrence: {
      frequency: values.frequency || 'daily',
      daysOfWeek: values.daysOfWeek || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      label: values.recurrenceLabel || 'Daily',
    },
    locationType,
    intakeSessionId: values.intakeSessionId || null,
    intakeSessionLabel: values.intakeSessionLabel || '',
    meetingUrl: ['online', 'hybrid'].includes(locationType) ? values.meetingUrl || '' : '',
    physicalLocation: ['physical', 'hybrid'].includes(locationType) ? values.physicalLocation || '' : '',
    room: ['physical', 'hybrid'].includes(locationType) ? values.room || '' : '',
    capacity: ['physical', 'hybrid'].includes(locationType) ? Number(values.capacity || 0) : 0,
    selfPaced: locationType === 'self_paced' ? {
      contentType: values.contentType || 'course materials',
      releaseDate: values.releaseDate || '',
      dueDate: values.dueDate || '',
    } : null,
    publicAccess: {
      enabled: values.publicAccessEnabled !== false,
      label: values.publicAccessLabel || 'Student login',
    },
    status: 'scheduled',
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getClasses(tenantId, countLimit = 50) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'classes'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function makeClassStudentVisible(tenantId, classId) {
  if (!tenantId || !classId) return;
  await updateDoc(doc(db, TENANTS, tenantId, 'classes', classId), {
    publicAccess: {
      enabled: true,
      label: 'Student login',
    },
    updatedAt: serverTimestamp(),
  });
}

// Student-facing class reads must be constrained by the same public-access
// field used by Firestore rules. Querying the whole admin collection can be
// rejected when an older class document does not have publicAccess.enabled.
export async function getStudentVisibleClasses(tenantId, countLimit = 100) {
  if (!tenantId) return [];
  const snapshot = await getDocs(query(
    collection(db, TENANTS, tenantId, 'classes'),
    where('publicAccess.enabled', '==', true),
    limit(countLimit),
  ));
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((item) => item.status !== 'deleted')
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
}

export async function getClass(tenantId, classId) {
  if (!tenantId || !classId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'classes', classId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function createClassJoin(tenantId, classId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'classes', classId, 'joins'), {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone || '',
    message: values.message || '',
    status: 'active',
    source: 'student_class_login',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getClassJoins(tenantId, classId, countLimit = 100) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'classes', classId, 'joins'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createStudent(tenantId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'students'), {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email || '',
    phone: values.phone || '',
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getStudents(tenantId, countLimit = 50) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'students'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getMembers(tenantId) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'members'), orderBy('joinedAt', 'desc'), limit(50)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getEmployees(tenantId) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'employees'), orderBy('createdAt', 'desc'), limit(100)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getEmployee(tenantId, employeeId) {
  if (!tenantId || !employeeId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'employees', employeeId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function createEmployee(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'employees'), {
    fullName: values.fullName,
    jobTitle: values.jobTitle || '',
    email: values.email || '',
    phone: values.phone || '',
    status: values.status || 'active',
    createdBy: userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateEmployee(tenantId, employeeId, userId, values) {
  await setDoc(doc(db, TENANTS, tenantId, 'employees', employeeId), {
    fullName: values.fullName || '',
    jobTitle: values.jobTitle || '',
    email: values.email || '',
    phone: values.phone || '',
    status: values.status || 'active',
    employmentType: values.employmentType || '',
    startDate: values.startDate || '',
    nationalId: values.nationalId || '',
    passportNumber: values.passportNumber || '',
    identityDocumentUrl: values.identityDocumentUrl || '',
    identityDocumentName: values.identityDocumentName || '',
    salary: Number(values.salary || 0),
    salaryFrequency: values.salaryFrequency || 'monthly',
    paymentMethod: values.paymentMethod || '',
    bankName: values.bankName || '',
    bankAccountName: values.bankAccountName || '',
    bankAccountNumber: values.bankAccountNumber || '',
    address: values.address || '',
    emergencyContactName: values.emergencyContactName || '',
    emergencyContactPhone: values.emergencyContactPhone || '',
    notes: values.notes || '',
    updatedBy: userId || null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function getRoles(tenantId) {
  const snapshot = await getDocs(collection(db, TENANTS, tenantId, 'roles'));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createInvitation(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'invitations'), {
    email: values.email,
    roleId: values.roleId,
    employeeId: values.employeeId || null,
    employeeName: values.employeeName || '',
    employeePhone: values.employeePhone || '',
    status: 'pending',
    invitedBy: userId,
    createdAt: serverTimestamp(),
  });
}

export async function getInvitations(tenantId) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'invitations'), orderBy('createdAt', 'desc'), limit(50)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function createTransaction(tenantId, userId, values) {
  return addDoc(collection(db, TENANTS, tenantId, 'transactions'), {
    receiptNumber: values.receiptNumber || '',
    invoiceNumber: values.invoiceNumber || '',
    type: values.type || 'revenue',
    category: values.category || 'school_fee',
    source: values.source || 'manual',
    studentId: values.studentId || null,
    authUid: values.authUid || null,
    studentName: values.studentName || '',
    studentEmail: values.studentEmail || '',
    studentPhone: values.studentPhone || '',
    employeeId: values.employeeId || null,
    employeeName: values.employeeName || '',
    employeeEmail: values.employeeEmail || '',
    employeePhone: values.employeePhone || '',
    salaryPeriod: values.salaryPeriod || '',
    salaryRate: Number(values.salaryRate || 0),
    courseId: values.courseId || null,
    courseName: values.courseName || '',
    amount: Number(values.amount || 0),
    paymentDate: values.paymentDate || '',
    dueDate: values.dueDate || '',
    paymentMethod: values.paymentMethod || 'cash',
    reference: values.reference || '',
    note: values.note || '',
    status: values.status || 'posted',
    recordedBy: userId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function getTransactions(tenantId, countLimit = 100) {
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'transactions'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getStudentTransactions(tenantId, authUid, email = '', studentIds = [], countLimit = 100) {
  if (!tenantId || (!authUid && !email)) return [];
  const queries = [];
  if (authUid) queries.push(query(collection(db, TENANTS, tenantId, 'transactions'), where('authUid', '==', authUid), limit(countLimit)));
  if (email) queries.push(query(collection(db, TENANTS, tenantId, 'transactions'), where('studentEmail', '==', email), limit(countLimit)));
  studentIds.filter(Boolean).forEach((studentId) => {
    queries.push(query(collection(db, TENANTS, tenantId, 'transactions'), where('studentId', '==', studentId), limit(countLimit)));
  });
  const results = await Promise.allSettled(queries.map((transactionQuery) => getDocs(transactionQuery)));
  const seen = new Set();
  return results.filter((result) => result.status === 'fulfilled').flatMap((result) => result.value.docs.map((item) => ({ id: item.id, ...item.data() }))).filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  }).sort((a, b) => {
    const aTime = a.createdAt?.toMillis?.() || 0;
    const bTime = b.createdAt?.toMillis?.() || 0;
    return bTime - aTime;
  });
}

export async function getAttendanceRecords(tenantId, countLimit = 500) {
  if (!tenantId) return [];
  const snapshot = await getDocs(query(collection(db, TENANTS, tenantId, 'attendance'), orderBy('createdAt', 'desc'), limit(countLimit)));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function getTransaction(tenantId, transactionId) {
  if (!tenantId || !transactionId) return null;
  const snapshot = await getDoc(doc(db, TENANTS, tenantId, 'transactions', transactionId));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

export async function linkTransactionToStudent(tenantId, transactionId, authUid) {
  if (!tenantId || !transactionId || !authUid) return;
  await updateDoc(doc(db, TENANTS, tenantId, 'transactions', transactionId), {
    authUid,
    updatedAt: serverTimestamp(),
  });
}

export async function getDashboardStats(tenantId) {
  const [activeCourses, students, upcomingClasses, transactions] = await Promise.all([
    getCountFromServer(query(collection(db, TENANTS, tenantId, 'courses'), where('status', '==', 'active'))),
    getCountFromServer(collection(db, TENANTS, tenantId, 'students')),
    getCountFromServer(query(collection(db, TENANTS, tenantId, 'classes'), where('status', '==', 'scheduled'))),
    getDocs(query(collection(db, TENANTS, tenantId, 'transactions'), limit(50))),
  ]);

  const revenue = transactions.docs.reduce((sum, transaction) => {
    const data = transaction.data();
    return data.type === 'revenue' ? sum + Number(data.amount || 0) : sum;
  }, 0);

  return {
    activeCourses: activeCourses.data().count,
    students: students.data().count,
    upcomingClasses: upcomingClasses.data().count,
    revenue,
  };
}
